import React from "react";
import Seo from "../components/Seo.jsx";

/* Scans screen — history table + drill-in detail view.
   Click "View" on a row → ScanDetail replaces the list, showing
   the funnel (candidates → filtered → surfaced), themes used,
   and the scan's surfaced opportunities. Every scan's results are
   fetched by scan id, so past scans show their matches too. */

function formatScanDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const isYesterday = d.toDateString() === yesterday.toDateString();
  const time = d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  if (sameDay) return `Today, ${time}`;
  if (isYesterday) return `Yesterday, ${time}`;
  const month = d.toLocaleDateString([], { month: "short", day: "numeric" });
  return `${month}, ${time}`;
}

// Human label for the scan dialog's time window so the history table can
// remind the user how far back each scan looked. Mirrors the dialog options
// in scan-dialog.jsx. Unknown / legacy scans (no stored window) → null.
function formatTimeFrame(window) {
  switch (window) {
    case "since-last": return "Since last scan";
    case "24h": return "Fresh · 24 hours";
    case "3d": return "Recent · 3 days";
    case "7d": return "Deep · 1 week";
    case "14d": return "Extended · 2 weeks";
    case "30d": return "Archive · 30 days";
    default: return null;
  }
}

function formatDuration(startIso, endIso) {
  if (!startIso || !endIso) return null;
  const a = new Date(startIso).getTime();
  const b = new Date(endIso).getTime();
  if (Number.isNaN(a) || Number.isNaN(b) || b < a) return null;
  const secs = Math.max(1, Math.round((b - a) / 1000));
  if (secs < 60) return `${secs}s`;
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return s ? `${m}m ${s}s` : `${m}m`;
}

function isPreResultRunning(scan) {
  return (
    scan?.status === "running" &&
    Number(scan?.candidates_count || 0) === 0 &&
    Number(scan?.surfaced_count || 0) === 0
  );
}

const SURFACED_TYPE_FILTERS = [
  { id: "all", label: "All" },
  { id: "post", label: "Posts" },
  { id: "comment", label: "Comments" },
];

function ScansScreen({
  onScanNow,
  isScanning,
  scanRefreshKey,
  onOpenOpp,
  onDeleteScan,
  onStopScan,
  onNavigate,
  routeScanId,
  onOpenScan,
  onBackToScans,
  demoScans,
  demoScanResultsById,
}) {
  const [scans, setScans] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  const profileId = window.api?.storage?.readProfileId?.();

  const refresh = React.useCallback(async () => {
    if (demoScans) {
      setScans(demoScans);
      setLoading(false);
      setError(null);
      return;
    }
    if (!window.api?.scan?.list) return;
    setLoading(true);
    setError(null);
    try {
      const data = await window.api.scan.list(profileId);
      setScans(Array.isArray(data?.scans) ? data.scans : []);
    } catch (e) {
      setError(e?.message || "Could not load scans");
      setScans([]);
    } finally {
      setLoading(false);
    }
  }, [demoScans, profileId]);

  React.useEffect(() => { refresh(); }, [refresh, scanRefreshKey]);

  const selected = routeScanId ? scans.find((s) => s.id === routeScanId) : null;

  // Once the real backend row exists (running / partial_ready) we drop the
  // optimistic placeholder so we never show two live rows.
  const hasLiveScanRow = scans.some(
    (s) => s.status === "running" || s.status === "partial_ready"
  );
  const showPendingRow = isScanning && !hasLiveScanRow && !error;

  if (selected) {
    const index = scans.findIndex((s) => s.id === routeScanId);
    const prev = scans[index + 1];
    return (
      <ScanDetail
        scan={selected}
        prev={prev}
        isLatest={index === 0}
        onBack={() => onBackToScans?.()}
        onRerun={onScanNow}
        onDeleteScan={onDeleteScan}
        onStopScan={onStopScan}
        isScanning={isScanning}
        onOpenOpp={onOpenOpp}
        onNavigate={onNavigate}
        demoScanResults={demoScanResultsById?.[selected.id] || null}
      />
    );
  }

  if (routeScanId && !loading && !error) {
    return (
      <div className="page screen-enter" style={{ maxWidth: 1080 }}>
        <Seo title="Scan not found" noindex />
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
          <button className="btn btn-ghost btn-sm" onClick={() => onBackToScans?.()}>
            <window.I.ChevronLeft size={14} />
            Back to scans
          </button>
        </div>
        <div className="card" style={{ padding: 24 }}>
          <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 6 }}>Scan not found</div>
          <div style={{ fontSize: 13, color: "var(--text-secondary)" }}>
            We couldn't find a scan with that id for the current profile.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page screen-enter" style={{ maxWidth: 1080 }}>
      <Seo title="Scans" noindex />
      <div className="page-header">
        <div>
          <h1 className="page-title">Scans</h1>
          <p className="page-subtitle">
            A scan runs automatically everyday. You can run one manually anytime.
          </p>
        </div>
        <button className="btn btn-primary btn-sm" onClick={onScanNow} disabled={isScanning}>
          {isScanning ? (
            <>
              <window.I.Sparkles size={14} style={{ animation: "spin 900ms linear infinite" }} />
              Scanning…
            </>
          ) : (
            <>
              <window.I.Sparkles size={14} />
              Run scan now
            </>
          )}
        </button>
      </div>

      <div className="card" style={{ overflow: "hidden" }}>
        <table className="scan-table">
          <thead>
            <tr>
              <th style={{ width: 180 }}>When</th>
              <th>Status</th>
              <th style={{ width: 150 }}>Time frame</th>
              <th className="num" style={{ textAlign: "center", width: 110 }}>Candidates</th>
              <th className="num" style={{ textAlign: "center", width: 110 }}>Surfaced</th>
              <th style={{ width: 80 }}></th>
            </tr>
          </thead>
          <tbody>
            {loading && scans.length === 0 && (
              <tr>
                <td colSpan={6} style={{ textAlign: "center", padding: 24, color: "var(--text-secondary)" }}>
                  Loading scans…
                </td>
              </tr>
            )}
            {!loading && error && (
              <tr>
                <td colSpan={6} style={{ textAlign: "center", padding: 24, color: "var(--warning-text)" }}>
                  <window.I.Alert size={12} style={{ verticalAlign: "-2px", marginRight: 4 }} />
                  {error}
                </td>
              </tr>
            )}
            {!loading && !error && scans.length === 0 && !showPendingRow && (
              <tr>
                <td colSpan={6} style={{ textAlign: "center", padding: 24, color: "var(--text-secondary)" }}>
                  No scans yet. Click <strong>Run scan now</strong> to kick one off.
                </td>
              </tr>
            )}
            {/* Optimistic row: the moment "Scan now" fires (isScanning), show a
                live "Scanning…" row so the user gets instant feedback instead
                of waiting ~2s for the backend to register the scan and the
                next list refresh to pick it up. It's dropped as soon as the
                real running/partial_ready row lands in `scans`. */}
            {showPendingRow && (
              <tr style={{ cursor: "default" }}>
                <td>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>
                    {formatScanDate(new Date().toISOString())}
                  </div>
                </td>
                <td>
                  <span className="pill pill-neutral">
                    <span
                      className="pill-dot"
                      style={{
                        background: "var(--accent)",
                        animation: "live-pulse 1400ms ease-in-out infinite",
                      }}
                    />
                    Starting…
                  </span>
                </td>
                <td>
                  <span style={{ color: "var(--text-tertiary)", fontSize: 13 }}>—</span>
                </td>
                <td className="num" style={{ textAlign: "center", color: "var(--text-secondary)" }}>
                  <span className="scan-live-label" aria-label="Scanning">
                    <span className="scan-live-label-dot" />
                    scanning
                  </span>
                </td>
                <td className="num" style={{ textAlign: "center" }}>—</td>
                <td />
              </tr>
            )}
            {scans.map((s) => {
              const isRunning = s.status === "running";
              const isWaitingForFirstWave = isPreResultRunning(s);
              const isFailed = s.status === "failed";
              const isPartial = s.status === "partial";
              // Stage 1 of a progressive scan completed; Stage 2 is still
              // running in the backend. Results are visible but the row
              // will tick up when the deep scan appends more.
              const isPartialReady = s.status === "partial_ready";
              const openDetail = () => onOpenScan?.(s.id);
              return (
                <tr
                  key={s.id}
                  onClick={openDetail}
                  style={{ cursor: "pointer" }}
                >
                  <td>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>
                      {formatScanDate(s.started_at)}
                    </div>
                    {s.error_message && (
                      <div style={{ fontSize: 11.5, color: "var(--warning-text)", marginTop: 2, maxWidth: 280 }}>
                        <window.I.Alert size={11} style={{ verticalAlign: "-2px", marginRight: 3 }} />
                        {s.error_message}
                      </div>
                    )}
                  </td>
                  <td>
                    {isRunning ? (
                      <span className="pill pill-neutral">
                        <span className="pill-dot" style={{ background: "var(--text-secondary)" }} />
                        Running
                      </span>
                    ) : isPartialReady ? (
                      <span className="pill pill-neutral">
                        <span
                          className="pill-dot"
                          style={{
                            background: "var(--accent)",
                            animation: "live-pulse 1400ms ease-in-out infinite",
                          }}
                        />
                        Initial results
                      </span>
                    ) : isFailed ? (
                      <span className="pill pill-warning">
                        <span className="pill-dot" style={{ background: "var(--danger)" }} />
                        Failed
                      </span>
                    ) : isPartial ? (
                      <span className="pill pill-warning">
                        <span className="pill-dot" style={{ background: "var(--warning)" }} />
                        Partial
                      </span>
                    ) : (
                      <span className="pill pill-success">
                        <span className="pill-dot" style={{ background: "var(--success)" }} />
                        Complete
                      </span>
                    )}
                  </td>
                  <td>
                    {formatTimeFrame(s.scan_window) ? (
                      <span className="pill pill-neutral" style={{ fontWeight: 500 }}>
                        <window.I.Clock size={11} style={{ color: "var(--text-tertiary)" }} />
                        {formatTimeFrame(s.scan_window)}
                      </span>
                    ) : (
                      <span style={{ color: "var(--text-tertiary)", fontSize: 13 }}>—</span>
                    )}
                  </td>
                  <td className="num" style={{ textAlign: "center", color: "var(--text-secondary)" }}>
                    {isWaitingForFirstWave ? (
                      <span className="scan-live-label" aria-label="Scanning">
                        <span className="scan-live-label-dot" />
                        scanning
                      </span>
                    ) : (
                      s.candidates_count ?? 0
                    )}
                  </td>
                  <td className="num" style={{ textAlign: "center", color: "var(--text-primary)", fontWeight: 500 }}>
                    {isWaitingForFirstWave ? "—" : (s.surfaced_count ?? 0)}
                  </td>
                  <td>
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                      {(isRunning || isPartialReady) && onStopScan ? (
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={async (e) => {
                            e.stopPropagation();
                            const ok = window.confirm(
                              "Stop this scan? Any results already saved will be kept, and you can delete the scan afterward."
                            );
                            if (!ok) return;
                            try {
                              await onStopScan(s);
                            } catch (err) {
                              window.alert("Could not stop scan: " + (err?.message || "unknown error"));
                            }
                          }}
                          style={{
                            color: "var(--danger-text, var(--danger))",
                            borderColor: "var(--danger-border, var(--border-subtle))",
                          }}
                        >
                          <window.I.StopCircle size={14} />
                          Stop
                        </button>
                      ) : null}
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={(e) => { e.stopPropagation(); openDetail(); }}
                      >
                        View
                        <window.I.ChevronRight size={12} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   Scan detail
   ───────────────────────────────────────────────────────────── */

function ScanDetail({
  scan,
  prev,
  isLatest,
  onBack,
  onRerun,
  onDeleteScan,
  onStopScan,
  isScanning,
  onOpenOpp,
  onNavigate,
  demoScanResults,
}) {
  const [surfaced, setSurfaced] = React.useState([]);
  const [surfacedLoading, setSurfacedLoading] = React.useState(false);
  const [surfacedError, setSurfacedError] = React.useState(null);
  const [deleting, setDeleting] = React.useState(false);
  const [stopping, setStopping] = React.useState(false);
  const [surfacedTypeFilter, setSurfacedTypeFilter] = React.useState("all");

  // Filtered-out candidates: lazily loaded when the "Filtered out" funnel
  // step is clicked, then shown in a modal list.
  const [filteredOpen, setFilteredOpen] = React.useState(false);
  const [filteredLoading, setFilteredLoading] = React.useState(false);
  const [filteredError, setFilteredError] = React.useState(null);
  const [filteredRows, setFilteredRows] = React.useState([]);

  // Fetch this scan's surfaced opportunities by scan id. opportunity_scan_results
  // is stored per scan with denormalized columns, so this works for any past
  // scan — not just the latest — and survives the raw content being pruned.
  React.useEffect(() => {
    let cancelled = false;
    if (demoScanResults) {
      setSurfaced(demoScanResults);
      setSurfacedError(null);
      setSurfacedLoading(false);
      return () => { cancelled = true; };
    }
    // partial_ready is a "Stage 1 done, Stage 2 still appending" snapshot —
    // its surfaced rows are already in opportunity_scan_results, so fetch
    // them just like a complete scan.
    const showable = scan.status === "complete" || scan.status === "partial_ready";
    if (!showable || !window.api?.opportunities?.forScan) {
      setSurfaced([]);
      return;
    }
    setSurfacedLoading(true);
    setSurfacedError(null);
    window.api.opportunities.forScan(scan.id, { onlySurfaced: true, limit: 800 })
      .then((data) => {
        if (cancelled) return;
        const rows = Array.isArray(data?.results) ? data.results : [];
        const mapped = window.api.opportunities.mapResults
          ? window.api.opportunities.mapResults(rows)
          : rows.map(window.api.opportunities.mapResult).filter(Boolean);
        setSurfaced(mapped);
      })
      .catch((e) => {
        if (cancelled) return;
        setSurfacedError(e?.message || "Could not load surfaced opportunities");
        setSurfaced([]);
      })
      .finally(() => {
        if (!cancelled) setSurfacedLoading(false);
      });
    return () => { cancelled = true; };
    // surfaced_count is in the deps so a live progress bump (the parent
    // re-fetches the scan list on each bump, updating this count) re-pulls the
    // surfaced rows — the detail page streams in step with the inbox.
  }, [demoScanResults, scan.id, scan.status, scan.surfaced_count]);

  const candidates = scan.candidates_count ?? 0;
  const surfacedCount = scan.surfaced_count ?? 0;
  const filteredTotal = Math.max(0, candidates - surfacedCount);
  const surfacePct = candidates ? (surfacedCount / candidates) * 100 : 0;
  const candidatesDelta = prev ? candidates - (prev.candidates_count ?? 0) : null;
  const surfacedDelta = prev ? surfacedCount - (prev.surfaced_count ?? 0) : null;
  const duration = formatDuration(scan.started_at, scan.finished_at);
  const isRunning = scan.status === "running";
  const isWaitingForFirstWave = isPreResultRunning(scan);
  const isFailed = scan.status === "failed";
  const isPartial = scan.status === "partial";
  // Stage 1 of a progressive scan done; Stage 2 is still appending. Treated
  // as "showable" everywhere so surfaced rows render — but pill stays neutral
  // and we surface a "still scanning" hint in the header.
  const isPartialReady = scan.status === "partial_ready";

  const isComplete = scan.status === "complete" || isPartialReady;
  const canDelete = !!onDeleteScan && !isRunning && !isPartialReady;
  const canStop = !!onStopScan && (isRunning || isPartialReady);
  const filteredSurfaced = React.useMemo(() => {
    if (surfacedTypeFilter === "all") return surfaced;
    return surfaced.filter((opp) => opp.kind === surfacedTypeFilter);
  }, [surfaced, surfacedTypeFilter]);

  const openFiltered = React.useCallback(() => {
    setFilteredOpen(true);
    if (!window.api?.opportunities?.filteredForScan) {
      setFilteredRows([]);
      return;
    }
    setFilteredLoading(true);
    setFilteredError(null);
    window.api.opportunities.filteredForScan(scan.id, { limit: 800 })
      .then((data) => {
        const rows = Array.isArray(data?.results) ? data.results : [];
        setFilteredRows(window.api.opportunities.mapFiltered(rows));
      })
      .catch((e) => {
        setFilteredError(e?.message || "Could not load filtered candidates");
        setFilteredRows([]);
      })
      .finally(() => setFilteredLoading(false));
  }, [scan.id]);

  const handleDelete = React.useCallback(async () => {
    if (!canDelete || deleting) return;
    const ok = window.confirm(
      "Delete this scan and its related stored results? Opportunities only found in this scan will also be removed from the inbox. This cannot be undone."
    );
    if (!ok) return;
    setDeleting(true);
    try {
      await onDeleteScan(scan);
    } catch (e) {
      window.alert("Could not delete scan: " + (e?.message || "unknown error"));
    } finally {
      setDeleting(false);
    }
  }, [canDelete, deleting, onDeleteScan, scan]);

  const handleStop = React.useCallback(async () => {
    if (!canStop || stopping) return;
    const ok = window.confirm(
      "Stop this scan? Any results already saved will be kept, and you can delete the scan afterward."
    );
    if (!ok) return;
    setStopping(true);
    try {
      await onStopScan(scan);
    } catch (e) {
      window.alert("Could not stop scan: " + (e?.message || "unknown error"));
    } finally {
      setStopping(false);
    }
  }, [canStop, onStopScan, scan, stopping]);

  return (
    <div className="page screen-enter" style={{ maxWidth: 1080 }}>
      {/* Breadcrumb */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
        <button className="btn btn-ghost btn-sm" onClick={onBack}>
          <window.I.ChevronLeft size={14} />
          Back to scans
        </button>
      </div>

      {/* Header */}
      <div className="page-header" style={{ alignItems: "flex-start" }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 8 }}>
            {isRunning ? (
              <span className="pill pill-neutral">
                <span className="pill-dot" style={{ background: "var(--text-secondary)" }} />
                Running
              </span>
            ) : isPartialReady ? (
              <span className="pill pill-neutral">
                <span
                  className="pill-dot"
                  style={{
                    background: "var(--accent)",
                    animation: "live-pulse 1400ms ease-in-out infinite",
                  }}
                />
                Initial results · deep scan running
              </span>
            ) : isFailed ? (
              <span className="pill pill-warning">
                <span className="pill-dot" style={{ background: "var(--danger)" }} />
                Failed
              </span>
            ) : isPartial ? (
              <span className="pill pill-warning">
                <span className="pill-dot" style={{ background: "var(--warning)" }} />
                Partial
              </span>
            ) : (
              <span className="pill pill-success">
                <span className="pill-dot" style={{ background: "var(--success)" }} />
                Complete
              </span>
            )}
            <span className="opp-meta" style={{ fontSize: 12.5 }}>
              {isLatest ? "Most recent" : "Past scan"}
            </span>
          </div>
          <h1 className="page-title">{formatScanDate(scan.started_at)}</h1>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 6,
              marginTop: 6,
            }}
          >
            {duration && (
              <>
                <span className="opp-meta">Took {duration}</span>
              </>
            )}
        
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {onStopScan && (
            <button
              className="btn btn-secondary btn-sm"
              onClick={handleStop}
              disabled={!canStop || stopping}
              title={!canStop ? "Only running scans can be stopped." : undefined}
              style={{
                color: "var(--danger-text, var(--danger))",
                borderColor: "var(--danger-border, var(--border-subtle))",
              }}
            >
              <window.I.StopCircle size={14} />
              {stopping ? "Stopping…" : "Stop"}
            </button>
          )}
          {onDeleteScan && (
            <button
              className="btn btn-secondary btn-sm"
              onClick={handleDelete}
              disabled={!canDelete || deleting}
              title={!canDelete ? "Wait for the scan to finish before deleting it." : undefined}
              style={{
                color: "var(--danger-text, var(--danger))",
                borderColor: "var(--danger-border, var(--border-subtle))",
              }}
            >
              <window.I.Trash size={14} />
              {deleting ? "Deleting…" : "Delete"}
            </button>
          )}
          <button className="btn btn-secondary btn-sm" onClick={onBack}>
            All scans
          </button>
          <button
            className="btn btn-primary btn-sm"
            onClick={onRerun}
            disabled={isScanning}
          >
            <window.I.Sparkles
              size={14}
              style={isScanning ? { animation: "spin 900ms linear infinite" } : null}
            />
            {isScanning ? "Scanning…" : "Re-run scan"}
          </button>
        </div>
      </div>

      {/* Error banner */}
      {scan.error_message && (
        <div
          className="card"
          style={{
            display: "flex", gap: 12, padding: 14, marginBottom: 16,
            borderColor: "var(--warning-border, var(--border-subtle))",
            background: "var(--warning-subtle, var(--bg-subtle))",
            color: "var(--warning-text)",
          }}
        >
          <window.I.Alert size={16} style={{ marginTop: 1, flex: "0 0 auto" }} />
          <div>
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 2 }}>
              {isFailed ? "Scan failed" : "Partial scan"}
            </div>
            <div style={{ fontSize: 12.5, lineHeight: 1.5 }}>{scan.error_message}</div>
          </div>
        </div>
      )}

      {/* Funnel */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header" style={{ borderBottom: "1px solid var(--border-subtle)" }}>
          <window.I.Filter size={14} style={{ color: "var(--text-tertiary)" }} />
          <span className="card-title" style={{ whiteSpace: "nowrap" }}>Candidate funnel</span>
          <div className="spacer" />
          <span className="opp-meta" style={{ fontSize: 12, whiteSpace: "nowrap" }}>
            {surfacePct.toFixed(1)}% surface rate
          </span>
        </div>
        {isWaitingForFirstWave ? (
          <div className="scan-pre-results">
            <span className="scan-live-label scan-live-label-hero" aria-label="Scanning">
              <span className="scan-live-label-dot" />
              scanning
            </span>
            <div className="scan-pre-results-copy">
              First pass is in flight. We&apos;ll swap this in for real candidate and surfaced counts as soon as the first wave lands.
            </div>
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 24px 1fr 24px 1fr",
              alignItems: "stretch",
              gap: 12,
              padding: 20,
            }}
          >
            <FunnelStep
              label="Candidates analyzed"
              value={candidates}
              delta={candidatesDelta}
              deltaLabel="vs. prior scan"
              color="var(--text-primary)"
              fill={100}
            />
            <FunnelArrow />
            <FunnelStep
              label="Filtered out"
              value={filteredTotal}
              sub="Below match threshold or off-topic"
              color="var(--text-secondary)"
              fill={candidates ? (filteredTotal / candidates) * 100 : 0}
              onClick={filteredTotal > 0 ? openFiltered : undefined}
            />
            <FunnelArrow />
            <FunnelStep
              label="Surfaced to feed"
              value={surfacedCount}
              delta={surfacedDelta}
              deltaLabel="vs. prior scan"
              color="var(--accent-soft-text, var(--accent))"
              fill={surfacePct}
              tone="accent"
            />
          </div>
        )}
      </div>

      {/* Surfaced opportunities */}
      <div className="card">
        <div className="card-header" style={{ borderBottom: "1px solid var(--border-subtle)" }}>
          <window.I.CheckCircle size={14} style={{ color: "var(--success)" }} />
          <span className="card-title" style={{ whiteSpace: "nowrap" }}>
            Surfaced · {surfacedCount}
          </span>
          <div className="spacer" />
          {!surfacedLoading && !surfacedError && surfaced.length > 0 && (
            <div className="scan-segmented" role="tablist" aria-label="Filter surfaced opportunities by type">
              {SURFACED_TYPE_FILTERS.map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  className={"scan-segmented-opt " + (surfacedTypeFilter === opt.id ? "is-on" : "")}
                  onClick={() => setSurfacedTypeFilter(opt.id)}
                  aria-pressed={surfacedTypeFilter === opt.id ? "true" : "false"}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
          <div className="spacer" />
          {onNavigate && (
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => onNavigate("opportunities")}
            >
              Open feed
              <window.I.ChevronRight size={12} />
            </button>
          )}
        </div>

        {surfacedLoading && (
          <div style={{ padding: 24, textAlign: "center", color: "var(--text-secondary)", fontSize: 13 }}>
            Loading surfaced opportunities…
          </div>
        )}

        {!surfacedLoading && surfacedError && (
          <div style={{ padding: 24, textAlign: "center", color: "var(--warning-text)", fontSize: 13 }}>
            <window.I.Alert size={12} style={{ verticalAlign: "-2px", marginRight: 4 }} />
            {surfacedError}
          </div>
        )}

        {!surfacedLoading && !surfacedError && isComplete && surfaced.length === 0 && (
          <div className="empty-state" style={{ border: "none", borderRadius: 0, padding: "36px 24px" }}>
            <div className="empty-icon"><window.I.Inbox size={20} /></div>
            <div className="empty-title">
              {isPartialReady ? "Initial pass found nothing yet" : "Nothing surfaced"}
            </div>
            <div className="empty-desc">
              {isPartialReady
                ? "The deep scan is still running and may surface more in a minute."
                : "No candidates cleared the match threshold this run. Try loosening filters or broadening themes in your profile."}
            </div>
          </div>
        )}

        {!surfacedLoading && !surfacedError && isComplete && surfaced.length > 0 && filteredSurfaced.length === 0 && (
          <div className="empty-state" style={{ border: "none", borderRadius: 0, padding: "36px 24px" }}>
            <div className="empty-icon"><window.I.Filter size={20} /></div>
            <div className="empty-title">No {surfacedTypeFilter === "post" ? "posts" : "comments"} in this scan</div>
            <div className="empty-desc">
              This scan surfaced opportunities, but none match the current type filter.
            </div>
          </div>
        )}

        {!surfacedLoading && !surfacedError && isComplete && filteredSurfaced.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column" }}>
            {filteredSurfaced.map((o) => (
              <SurfacedRow key={o.id} opp={o} onOpen={onOpenOpp} />
            ))}
          </div>
        )}

        {!surfacedLoading && (isRunning || isFailed) && (
          <div className="empty-state" style={{ border: "none", borderRadius: 0, padding: "28px 24px" }}>
            <div className="empty-icon"><window.I.Inbox size={20} /></div>
            <div className="empty-title">
              {isRunning ? "Scan still running" : "No results"}
            </div>
            <div className="empty-desc">
              {isRunning
                ? "Hang tight — refresh the list once it finishes."
                : "This scan didn't complete, so no opportunities were stored."}
            </div>
          </div>
        )}
      </div>

      {filteredOpen && (
        <FilteredOutModal
          total={filteredTotal}
          rows={filteredRows}
          loading={filteredLoading}
          error={filteredError}
          onClose={() => setFilteredOpen(false)}
          onNavigate={onNavigate}
        />
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   Filtered-out candidates modal
   ───────────────────────────────────────────────────────────── */

const FILTERED_KIND_FILTERS = [
  { id: "all", label: "All" },
  { id: "post", label: "Posts" },
  { id: "comment", label: "Comments" },
];

function FilteredOutModal({ total, rows, loading, error, onClose, onNavigate }) {
  const [kind, setKind] = React.useState("all");
  const [subreddit, setSubreddit] = React.useState("all");
  const [detail, setDetail] = React.useState(null);
  const [genMode, setGenMode] = React.useState(null);

  // Esc closes the list (but only when no draft dialog / detail is layered).
  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (genMode) setGenMode(null);
      else if (detail) setDetail(null);
      else onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [genMode, detail, onClose]);

  const subreddits = React.useMemo(
    () => Array.from(new Set(rows.map((r) => r.subreddit).filter(Boolean))).sort((a, b) => a.localeCompare(b)),
    [rows]
  );

  const visible = React.useMemo(
    () => rows.filter((r) => {
      const okKind = kind === "all" || r.kind === kind;
      const okSub = subreddit === "all" || r.subreddit === subreddit;
      return okKind && okSub;
    }),
    [rows, kind, subreddit]
  );

  const detailOpp = detail && window.api?.opportunities?.filteredToOpp
    ? window.api.opportunities.filteredToOpp(detail)
    : null;
  const profileId = window.api?.storage?.readProfileId?.() || null;

  return (
    <>
      <div className="detail-overlay is-open" style={{ position: "fixed", zIndex: 200 }} onClick={onClose} />
      <div
        role="dialog"
        aria-label="Filtered out candidates"
        style={{
          position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
          width: "min(640px, calc(100vw - 32px))", maxHeight: "calc(100vh - 64px)",
          display: "flex", flexDirection: "column",
          background: "var(--bg-elevated, var(--bg))", border: "1px solid var(--border)",
          borderRadius: "var(--r-xl, 16px)", boxShadow: "0 24px 60px rgba(0,0,0,0.35)",
          zIndex: 201, overflow: "hidden",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "16px 18px", borderBottom: "1px solid var(--border-subtle)" }}>
          <window.I.Filter size={15} style={{ color: "var(--text-tertiary)" }} />
          <div style={{ fontSize: 15, fontWeight: 600, color: "var(--text-primary)" }}>
            Filtered out · {total.toLocaleString()}
          </div>
          <div style={{ flex: 1 }} />
          <button className="btn btn-ghost btn-icon" onClick={onClose} title="Close (Esc)">
            <window.I.X size={16} />
          </button>
        </div>

        <div style={{ padding: "12px 18px 14px", borderBottom: "1px solid var(--border-subtle)" }}>
          <div style={{ fontSize: 12.5, color: "var(--text-secondary)", lineHeight: 1.5, marginBottom: 10 }}>
            Candidates this scan checked but didn&apos;t surface. Most never reached the deeper relevance pass.
          </div>
          {!loading && !error && rows.length > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <div className="scan-segmented" role="tablist" aria-label="Filter by type" style={{ flex: "0 0 auto" }}>
                {FILTERED_KIND_FILTERS.map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    className={"scan-segmented-opt " + (kind === opt.id ? "is-on" : "")}
                    onClick={() => setKind(opt.id)}
                    aria-pressed={kind === opt.id ? "true" : "false"}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              {subreddits.length > 1 && (
                <select
                  className="select-input"
                  value={subreddit}
                  onChange={(e) => setSubreddit(e.target.value)}
                  style={{
                    fontSize: 12.5, padding: "7px 10px", borderRadius: "var(--r-md)",
                    border: "1px solid var(--border)", background: "var(--bg-card)", color: "var(--text-primary)",
                  }}
                >
                  <option value="all">All subreddits</option>
                  {subreddits.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              )}
            </div>
          )}
        </div>

        <div className="scroll" style={{ overflowY: "auto", padding: 0 }}>
          {loading ? (
            <div style={{ padding: 28, textAlign: "center", color: "var(--text-secondary)", fontSize: 13 }}>
              Loading filtered candidates…
            </div>
          ) : error ? (
            <div style={{ padding: 28, textAlign: "center", color: "var(--warning-text)", fontSize: 13 }}>
              <window.I.Alert size={12} style={{ verticalAlign: "-2px", marginRight: 4 }} />
              {error}
            </div>
          ) : rows.length === 0 ? (
            <div style={{ padding: 28, textAlign: "center", color: "var(--text-secondary)", fontSize: 13 }}>
              No filtered candidates were stored for this scan.
            </div>
          ) : visible.length === 0 ? (
            <div style={{ padding: 28, textAlign: "center", color: "var(--text-secondary)", fontSize: 13 }}>
              No candidates match the current filter.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column" }}>
              {visible.map((row) => (
                <FilteredCandidateRow key={row.id} row={row} onOpen={() => setDetail(row)} />
              ))}
            </div>
          )}
        </div>
      </div>

      {detail && (
        <FilteredCandidateDetail
          row={detail}
          onClose={() => setDetail(null)}
          onGenerate={setGenMode}
        />
      )}

      {genMode && detailOpp && window.GenerateResponseDialog && (
        <window.GenerateResponseDialog
          mode={genMode}
          opp={detailOpp}
          profileId={profileId}
          onNavigate={onNavigate}
          onClose={() => setGenMode(null)}
        />
      )}
    </>
  );
}

function filteredTierColors(tier) {
  if (tier === "high") return { bg: "var(--accent-subtle, var(--bg-subtle))", color: "var(--accent)" };
  if (tier === "medium") return { bg: "var(--bg-subtle)", color: "var(--text-primary)" };
  return { bg: "var(--bg-subtle)", color: "var(--text-secondary)" };
}

function FilteredCandidateRow({ row, onOpen }) {
  const { bg, color } = filteredTierColors(row.tier);
  return (
    <button
      type="button"
      onClick={onOpen}
      style={{
        display: "flex", alignItems: "flex-start", gap: 12, width: "100%",
        padding: "12px 18px", background: "transparent", border: "none",
        borderBottom: "1px solid var(--border-subtle)", textAlign: "left", cursor: "pointer",
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11.5, color: "var(--text-tertiary)", marginBottom: 3, display: "flex", gap: 6, flexWrap: "wrap" }}>
          <span style={{ textTransform: "uppercase", letterSpacing: 0.3, fontWeight: 600 }}>{row.kind}</span>
          <span>·</span>
          <span>{row.subreddit}</span>
          <span>·</span>
          <span>{row.posted}</span>
        </div>
        <div style={{ fontSize: 13.5, fontWeight: 500, color: "var(--text-primary)", lineHeight: 1.4, marginBottom: 3 }}>
          {row.title}
        </div>
        {row.excerpt && (
          <div style={{
            fontSize: 12.5, color: "var(--text-secondary)", lineHeight: 1.45,
            display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
          }}>
            {row.excerpt}
          </div>
        )}
      </div>
      <div style={{
        fontVariantNumeric: "tabular-nums", fontSize: 12, fontWeight: 600,
        padding: "3px 8px", borderRadius: 6, background: bg, color, flex: "0 0 auto",
      }}>
        {row.score}%
      </div>
      <div style={{ color: "var(--text-tertiary)", flex: "0 0 auto", marginTop: 2 }}>
        <window.I.ChevronRight size={14} />
      </div>
    </button>
  );
}

function FilteredCandidateDetail({ row, onClose, onGenerate }) {
  const url = mobilePermalinkUrlForFiltered(row.permalink);
  const recipient = String(row.authorName || "").trim().replace(/^\/?u\//i, "").trim() || null;
  return (
    <>
      <div className="detail-overlay is-open" style={{ position: "fixed", zIndex: 210 }} onClick={onClose} />
      <div
        role="dialog"
        aria-label="Filtered candidate detail"
        style={{
          position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
          width: "min(560px, calc(100vw - 32px))", maxHeight: "calc(100vh - 80px)",
          display: "flex", flexDirection: "column",
          background: "var(--bg-elevated, var(--bg))", border: "1px solid var(--border)",
          borderRadius: "var(--r-xl, 16px)", boxShadow: "0 24px 60px rgba(0,0,0,0.35)",
          zIndex: 211, overflow: "hidden",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 18px", borderBottom: "1px solid var(--border-subtle)" }}>
          <div style={{ fontSize: 11.5, color: "var(--text-tertiary)", display: "flex", gap: 6, flexWrap: "wrap", flex: 1 }}>
            <span style={{ fontWeight: 600 }}>{row.score}% match</span>
            <span>·</span>
            <span>{row.subreddit}</span>
            <span>·</span>
            <span>{row.posted}</span>
          </div>
          <button className="btn btn-ghost btn-icon" onClick={onClose} title="Close (Esc)">
            <window.I.X size={16} />
          </button>
        </div>

        <div className="scroll" style={{ overflowY: "auto", padding: 18 }}>
          <h3 style={{ margin: "0 0 12px 0", fontSize: 18, fontWeight: 600, color: "var(--text-primary)", lineHeight: 1.35 }}>
            {row.title}
          </h3>
          {row.body && (
            <div className="thread-excerpt" style={{ marginBottom: 12 }}>
              <span className="quote-mark">“</span>
              <div style={{ paddingLeft: 14, whiteSpace: "pre-wrap" }}>{row.body}</div>
            </div>
          )}
          <div style={{ fontSize: 12, color: "var(--text-tertiary)", display: "flex", gap: 6, flexWrap: "wrap" }}>
            <span>{row.comments} comments</span>
            <span>·</span>
            <span>{row.upvotes} upvotes</span>
            {recipient && (<><span>·</span><span>u/{recipient}</span></>)}
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, padding: "14px 18px", borderTop: "1px solid var(--border-subtle)" }}>
          <button
            className="btn btn-secondary btn-sm"
            type="button"
            onClick={() => onGenerate("comment")}
            title="Draft a public Reddit reply for this candidate"
          >
            <window.I.Sparkles size={14} />
            Generate Comment
          </button>
          {recipient && (
            <button
              className="btn btn-secondary btn-sm"
              type="button"
              onClick={() => onGenerate("pm")}
              title={`Draft a private message to u/${recipient}`}
            >
              <window.I.Mail size={14} />
              Generate PM
            </button>
          )}
          <div style={{ flex: 1 }} />
          <a
            className="btn btn-primary btn-sm"
            href={url || "#"}
            target={url ? "_blank" : undefined}
            rel={url ? "noopener noreferrer" : undefined}
            onClick={(e) => { if (!url) e.preventDefault(); }}
            aria-disabled={!url}
            style={!url ? { opacity: 0.5, pointerEvents: "none" } : null}
          >
            <window.I.External size={14} />
            Open on Reddit
          </a>
        </div>
      </div>
    </>
  );
}

function mobilePermalinkUrlForFiltered(raw) {
  if (!raw) return null;
  return raw.startsWith("http")
    ? raw
    : `https://www.reddit.com${raw.startsWith("/") ? "" : "/"}${raw}`;
}

/* ─────────────────────────────────────────────────────────────
   Funnel primitives
   ───────────────────────────────────────────────────────────── */

function FunnelStep({ label, value, sub, delta, deltaLabel, color, fill, tone, onClick }) {
  const isAccent = tone === "accent";
  const fillColor = isAccent ? "var(--accent)" : "var(--border-strong, var(--border-subtle))";
  const clickable = typeof onClick === "function";
  return (
    <div
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={clickable ? onClick : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } } : undefined}
      className={clickable ? "scan-funnel-step-clickable" : undefined}
      style={{
        display: "flex", flexDirection: "column", justifyContent: "space-between",
        padding: 14, border: "1px solid var(--border-subtle)", borderRadius: 8,
        background: "var(--bg-elevated, var(--bg-card))", minHeight: 110,
        cursor: clickable ? "pointer" : "default",
        transition: "border-color 140ms ease, background 140ms ease",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "space-between" }}>
        <div style={{ fontSize: 11.5, color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: 0.4 }}>
          {label}
        </div>
        {clickable && <window.I.ChevronRight size={14} style={{ color: "var(--text-tertiary)", flexShrink: 0 }} />}
      </div>
      <div style={{ fontSize: 28, fontWeight: 600, color, lineHeight: 1.1, marginTop: 6 }}>
        {value.toLocaleString()}
      </div>
      <div style={{ fontSize: 11.5, color: "var(--text-secondary)", marginTop: 4, minHeight: 16 }}>
        {delta != null ? (
          <span style={{ color: delta > 0 ? "var(--success)" : delta < 0 ? "var(--danger)" : "var(--text-tertiary)" }}>
            {delta > 0 ? "▲" : delta < 0 ? "▼" : "—"} {Math.abs(delta)}
            <span style={{ color: "var(--text-tertiary)", marginLeft: 4 }}>{deltaLabel}</span>
          </span>
        ) : clickable && value > 0 ? (
          <span style={{ color: "var(--accent-soft-text, var(--accent))", fontWeight: 500 }}>View list →</span>
        ) : sub ? (
          <span style={{ color: "var(--text-tertiary)" }}>{sub}</span>
        ) : null}
      </div>
      <div style={{ height: 4, marginTop: 10, borderRadius: 2, background: "var(--border-subtle)", overflow: "hidden" }}>
        <div style={{
          height: "100%",
          width: `${Math.max(2, Math.min(100, fill))}%`,
          background: fillColor,
          transition: "width 320ms ease",
        }} />
      </div>
    </div>
  );
}

function FunnelArrow() {
  return (
    <div
      aria-hidden="true"
      style={{ display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text-tertiary)" }}
    >
      <window.I.ChevronRight size={18} />
    </div>
  );
}

function SurfacedRow({ opp, onOpen }) {
  const tier = opp.tier || "med";
  const tierBg =
    tier === "high" ? "var(--accent-subtle, var(--bg-subtle))" :
    tier === "medium" ? "var(--bg-subtle)" :
    "var(--bg-subtle)";
  const tierColor =
    tier === "high" ? "var(--accent)" :
    tier === "medium" ? "var(--text-primary)" :
    "var(--text-secondary)";
  const canOpen = !!onOpen && !!opp.id;
  const handleClick = () => {
    // Pass the whole opp — it may belong to a past scan that isn't in the
    // latest feed, so the detail panel can't resolve it by id alone.
    if (canOpen) onOpen(opp);
  };
  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={!canOpen}
      style={{
        display: "flex", alignItems: "center", gap: 12, width: "100%",
        padding: "12px 16px", background: "transparent", border: "none",
        borderBottom: "1px solid var(--border-subtle)", textAlign: "left",
        cursor: canOpen ? "pointer" : "default",
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11.5, color: "var(--text-tertiary)", marginBottom: 2 }}>
          {opp.subreddit}
          <span style={{ marginLeft: 6 }}>
            · {opp.kind}
          </span>
        </div>
        <div style={{
          fontSize: 13.5, fontWeight: 500, color: "var(--text-primary)",
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }}>
          {opp.title}
        </div>
      </div>
      <div style={{
        fontVariantNumeric: "tabular-nums", fontSize: 12, fontWeight: 600,
        padding: "3px 8px", borderRadius: 6,
        background: tierBg, color: tierColor, flex: "0 0 auto",
      }}>
        {opp.score}
      </div>
      {canOpen && (
        <div style={{ color: "var(--text-tertiary)", flex: "0 0 auto" }}>
          <window.I.ChevronRight size={14} />
        </div>
      )}
    </button>
  );
}

window.ScansScreen = ScansScreen;
